import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { Card, CardBody, CardHeader } from '@/design-system/components/Card'
import { Button } from '@/design-system/components/Button'
import { Badge } from '@/design-system/components/Badge'
import { AlertBanner } from '@/design-system/components/AlertBanner'

// ─── Types ────────────────────────────────────────────────────────────────────

interface Submission {
  id: string
  mission_id: string
  photographer_id: string
  status: string
  images: string[] | null
  ai_score: number | null
  reviewer_notes: string | null
  submitted_at: string
  reviewed_at: string | null
  payout_amount: number | null
  approved_at: string | null
  missions: {
    title: string
    reward_amount: number | null
    reward_currency: string | null
  } | null
  profiles: {
    full_name: string | null
    email: string | null
  } | null
}

interface ApprovePayload {
  submissionId: string
  payoutAmount: number | null
}

// ─── API helpers ──────────────────────────────────────────────────────────────

async function fetchAdminSubmissions(status: string): Promise<Submission[]> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const query = (supabase as any)
    .from('mission_submissions')
    .select(
      '*, missions(title, reward_amount, reward_currency), profiles!mission_submissions_photographer_id_fkey(full_name, email)'
    )
    .order('submitted_at', { ascending: false })
    .limit(100)

  const filtered = status === 'all' ? query : query.eq('status', status)
  const { data, error } = await filtered
  if (error) throw error
  return data as Submission[]
}

async function approveSubmission({ submissionId, payoutAmount }: ApprovePayload): Promise<unknown> {
  const { data, error } = await supabase.rpc('approve_submission' as never, {
    p_submission_id: submissionId,
    p_admin_id: (await supabase.auth.getUser()).data.user?.id,
    p_payout_amount: payoutAmount,
  } as never)
  if (error) throw error
  return data
}

// ─── Component ────────────────────────────────────────────────────────────────

const STATUS_FILTERS = ['pending', 'approved', 'rejected', 'all'] as const
type StatusFilter = typeof STATUS_FILTERS[number]

const STATUS_LABELS: Record<StatusFilter, string> = {
  pending: 'Väntande',
  approved: 'Godkända',
  rejected: 'Avvisade',
  all: 'Alla',
}

const BADGE_VARIANT: Record<string, 'pending' | 'complete' | 'urgent' | 'default'> = {
  pending: 'pending',
  approved: 'complete',
  rejected: 'urgent',
}

function ApproveModal({
  submission,
  onConfirm,
  onClose,
  isPending,
}: {
  submission: Submission
  onConfirm: (payout: number | null) => void
  onClose: () => void
  isPending: boolean
}) {
  const defaultPayout =
    submission.missions?.reward_amount != null
      ? submission.missions.reward_amount / 100
      : 0

  const [payout, setPayout] = useState(defaultPayout.toFixed(2))

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-surface-raised border border-surface-border rounded-xl w-full max-w-md shadow-[0_16px_48px_rgba(0,0,0,0.6)]">
        <div className="px-5 py-4 border-b border-surface-border">
          <h3 className="text-sm font-semibold text-text-primary">Godkänn inlämning + Betala ut</h3>
          <p className="text-xs text-text-muted mt-0.5">
            {submission.missions?.title ?? 'Okänt uppdrag'} —{' '}
            {submission.profiles?.full_name ?? 'Okänd Zoomer'}
          </p>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1">
              Utbetalningsbelopp ({submission.missions?.reward_currency ?? 'USD'})
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={payout}
              onChange={(e) => setPayout(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-surface-overlay border border-surface-border text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-brand-accent"
            />
            <p className="text-xs text-text-muted mt-1">
              Beloppet krediteras Zoomerns plånbok direkt.
            </p>
          </div>
          {submission.images && submission.images.length > 0 && (
            <div>
              <p className="text-xs font-medium text-text-secondary mb-2">
                Bilder ({submission.images.length} st)
              </p>
              <div className="flex gap-2 flex-wrap">
                {submission.images.slice(0, 4).map((url, i) => (
                  <img
                    key={i}
                    src={url}
                    alt={`Bild ${i + 1}`}
                    className="w-16 h-16 object-cover rounded-lg border border-surface-border"
                  />
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="px-5 pb-5 flex gap-3 justify-end">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={isPending}>
            Avbryt
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={isPending}
            onClick={() => onConfirm(payout ? parseFloat(payout) : null)}
          >
            {isPending ? 'Godkänner…' : '✓ Godkänn + Betala ut'}
          </Button>
        </div>
      </div>
    </div>
  )
}

export function SubmissionManagement() {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending')
  const [approveTarget, setApproveTarget] = useState<Submission | null>(null)
  const [successMsg, setSuccessMsg] = useState<string | null>(null)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const queryClient = useQueryClient()

  const { data = [], isLoading } = useQuery({
    queryKey: ['admin-submissions', statusFilter],
    queryFn: () => fetchAdminSubmissions(statusFilter),
    staleTime: 30_000,
  })

  const approveMutation = useMutation({
    mutationFn: approveSubmission,
    onSuccess: (result: unknown) => {
      const r = result as { success?: boolean; error?: string; payout_amount?: number; new_balance?: number }
      if (r?.success === false) {
        setErrorMsg(`Fel: ${r.error}`)
      } else {
        setSuccessMsg(
          `Godkänd! Utbetalat: ${r?.payout_amount ?? '?'} — Nytt saldo: ${r?.new_balance?.toFixed(2) ?? '?'}`
        )
        queryClient.invalidateQueries({ queryKey: ['admin-submissions'] })
      }
      setApproveTarget(null)
      setTimeout(() => { setSuccessMsg(null); setErrorMsg(null) }, 5000)
    },
    onError: (err: Error) => {
      setErrorMsg(`API-fel: ${err.message}`)
      setApproveTarget(null)
    },
  })

  const handleApprove = (payout: number | null) => {
    if (!approveTarget) return
    approveMutation.mutate({ submissionId: approveTarget.id, payoutAmount: payout })
  }

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleString('sv-SE', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })

  const formatReward = (sub: Submission) => {
    if (sub.payout_amount) return `${sub.payout_amount} ${sub.missions?.reward_currency ?? ''}`
    if (sub.missions?.reward_amount) {
      return `${(sub.missions.reward_amount / 100).toFixed(2)} ${sub.missions.reward_currency ?? ''}`
    }
    return '—'
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-text-primary">Inlämningar</h1>
        <p className="text-sm text-text-secondary mt-1">
          Granska och godkänn Zoomers inlämningar — godkännande krediterar plånboken direkt.
        </p>
      </div>

      {successMsg && (
        <AlertBanner variant="success" title="Klart!" description={successMsg} />
      )}
      {errorMsg && (
        <AlertBanner variant="danger" title="Något gick fel" description={errorMsg} />
      )}

      <Card>
        <CardHeader className="flex flex-wrap gap-2">
          {STATUS_FILTERS.map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                statusFilter === s
                  ? 'bg-brand-accent text-white'
                  : 'bg-surface-overlay text-text-secondary hover:text-text-primary border border-surface-border'
              }`}
            >
              {STATUS_LABELS[s]}
            </button>
          ))}
        </CardHeader>
        <CardBody className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="h-14 rounded-lg bg-surface-overlay animate-pulse" />
              ))}
            </div>
          ) : data.length === 0 ? (
            <div className="py-12 text-center text-text-muted text-sm">
              Inga inlämningar i denna kategori
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-surface-border">
                    <th className="text-left px-5 py-3 text-xs font-semibold text-text-muted">Uppdrag</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-text-muted">Fotograf</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-text-muted">AI-poäng</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-text-muted">Ersättning</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-text-muted">Inlämnad</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-text-muted">Status</th>
                    <th className="px-5 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-surface-border">
                  {data.map((sub) => (
                    <tr key={sub.id} className="hover:bg-surface-overlay/30 transition-colors">
                      <td className="px-5 py-3.5">
                        <p className="font-medium text-text-primary truncate max-w-[180px]">
                          {sub.missions?.title ?? '—'}
                        </p>
                        <p className="text-xs text-text-muted font-mono">{sub.mission_id.slice(0, 8)}…</p>
                      </td>
                      <td className="px-5 py-3.5">
                        <p className="text-text-primary">{sub.profiles?.full_name ?? '—'}</p>
                        <p className="text-xs text-text-muted">{sub.profiles?.email ?? ''}</p>
                      </td>
                      <td className="px-5 py-3.5 text-text-secondary">
                        {sub.ai_score != null ? `${Math.round(sub.ai_score * 100)}%` : '—'}
                      </td>
                      <td className="px-5 py-3.5 text-text-secondary">{formatReward(sub)}</td>
                      <td className="px-5 py-3.5 text-text-muted text-xs">{formatDate(sub.submitted_at)}</td>
                      <td className="px-5 py-3.5">
                        <Badge variant={BADGE_VARIANT[sub.status] ?? 'default'}>
                          {sub.status === 'pending' ? 'Väntar' : sub.status === 'approved' ? 'Godkänd' : sub.status === 'rejected' ? 'Avvisad' : sub.status}
                        </Badge>
                      </td>
                      <td className="px-5 py-3.5">
                        {sub.status === 'pending' && (
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => setApproveTarget(sub)}
                          >
                            Godkänn + Betala ut
                          </Button>
                        )}
                        {sub.status === 'approved' && sub.payout_amount != null && (
                          <span className="text-xs text-brand-success font-medium">
                            ✓ {sub.payout_amount} utbetalt
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {approveTarget && (
        <ApproveModal
          submission={approveTarget}
          onConfirm={handleApprove}
          onClose={() => setApproveTarget(null)}
          isPending={approveMutation.isPending}
        />
      )}
    </div>
  )
}
